import { inject, Injectable } from '@angular/core';
import { of } from 'rxjs';
import { Router, UrlSerializer, UrlTree } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { AuthActions } from '../actions';
import { AppActions } from '../../actions/';
import { AuthService } from '../../services';
import { CustomerActions, ShopCartActions } from '../../actions';
import * as OrderApproveActions from '../../actions/order-approve.actions';
import * as OrdersActions from '../../actions/orders.actions';
import { RedirectLoginResult, User } from '@auth0/auth0-spa-js';
import { INotification } from '../../models/cpq.models';
import { ConfigurationFacade } from 'src/app/facades/configuration.facade';
import { EFeatureToggles } from 'src/app/configurations/common';

@Injectable()
export class AuthEffects {

  private readonly actions$: Actions = inject(Actions);
  private readonly router: Router = inject(Router);
  private readonly authService: AuthService = inject(AuthService);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly urlSerializer: UrlSerializer = inject(UrlSerializer);

  handleAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.handleAuth),
      exhaustMap(() => {
        const params: string = globalThis.location.search;
        if (params.includes('code=') && params.includes('state=')) {
          return this.authService.handleRedirectCallback().pipe(
            map((cbRes: RedirectLoginResult) => {
              const targetRoute: string = cbRes.appState?.target ?? '/';
              return AuthActions.handleRedirectSuccess({
                targetRoute,
                queryParameters: cbRes?.appState?.queryParams,
              });
            }),
            catchError((err: any) => this.notifyError(`\n'Authentication Redirect'.\n${err}`)),
          );
        }
        return [AuthActions.checkAuth(), AuthActions.setIsLoginInProcess({isLoginInProcess: true})];
      }),
    ),
  );

  notifyError = (name: string) => this.notify({
    type: 'canceled',
    title: 'alert.error',
    messages: [
      {
        key: 'error.action',
        params: {name},
        css: ['message'],
      },
    ],
  });

  notify = (notification: INotification) => of(AppActions.appendNotification({notification}));

  handleRedirectSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.handleRedirectSuccess),
        tap(({targetRoute, queryParameters}) => {
          this.forceRedirectToTargetRoute(targetRoute, queryParameters);
        }),
      ),
    {dispatch: false},
  );

  login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logIn),
        tap((action) => {
          const urlTree: UrlTree = this.router.parseUrl(this.router.url);
          const redirectPath: string = action.url === '' ? `${globalThis.location.pathname}` : action.url;
          this.authService.login(
            redirectPath,
            !!action.queryParameters && !!Object.keys(action.queryParameters)?.length ?
              action.queryParameters : urlTree.queryParams,
          );
        }),
      ),
    {dispatch: false},
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUser, AuthActions.checkAuthSuccess, AuthActions.loadAccessTokenSuccess),
      mergeMap(() => this.authService.getUser$()
        .pipe(
          exhaustMap((user: User | undefined) => {
            if (user) {
              return [
                AuthActions.loadUserSuccess({user}),
                CustomerActions.loadCustomerData(),
              ];
            }

            return [];
          }),
          catchError((_: any) => [
            AuthActions.setAuthenticated({isAuthenticated: false}),
          ]),
        ),
      ),
    ),
  );

  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkAuth, AuthActions.handleRedirectSuccess),
      switchMap(() => {
        return this.authService.getTokenSilently$.pipe(
          exhaustMap((token: string) => {
              if (token) {
                return [
                  AuthActions.loadAccessTokenSuccess({token}),
                  AuthActions.loadUser(),
                  AuthActions.setAuthenticated({isAuthenticated: true}),
                  AuthActions.setIsLoginInProcess({isLoginInProcess: false}),
                ];
              }
              return [
                AuthActions.setAuthenticated({isAuthenticated: false}),
                CustomerActions.clearCompanyRoles(),
                AuthActions.setIsLoginInProcess({isLoginInProcess: false}),
              ];
            },
          ),
          catchError((_: any) => {
            return [
              CustomerActions.CompanyUsersActionStart(),
              ShopCartActions.clearCart(),
              OrderApproveActions.successSummaryPageClearData(),
              AuthActions.setAuthenticated({isAuthenticated: false}),
              OrdersActions.OrdersWithSapDetailsClearData(),
              OrdersActions.OrdersHistoryClearDataAction(),
              AuthActions.setIsLoginInProcess({isLoginInProcess: false}),
            ];
          }),
        );
      }),
    ),
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logOut),
        exhaustMap(({ skipRedirectToStore }) =>
          this.configurationFacade.isFeatureEnabled(EFeatureToggles.SINGLE_LOGOUT).pipe(
            take(1),
            filter((isSingleLogout: boolean) => !isSingleLogout),
            switchMap(() =>
              this.authService.logout(skipRedirectToStore).pipe(
                map(() => AuthActions.setIsLoginInProcess({ isLoginInProcess: false }))
              )
            )
          )
        )
      ),
    { dispatch: false }
  );

  forceRedirectToTargetRoute(targetRoute: string, queryParameters: any): void {
    const urlTree: UrlTree = this.router.createUrlTree([targetRoute], {queryParams: queryParameters});
    globalThis.location.href = this.urlSerializer.serialize(urlTree);
  }
}
